import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ArrowLeftRight, BookOpen, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { useHydrated } from "@/hooks/use-hydrated";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { modeLabel, type ExchangeMode } from "@/lib/constants";
import { pageHead } from "@/lib/seo";

export const Route = createFileRoute("/exchanges")({
  head: () => pageHead({ path: "/exchanges", title: "Chosen books — Book Swap", description: "See exactly which book was chosen in return for every swap, lend or donation on Book Swap.", noindex: true }),
  component: ExchangesPage,
});

type BookLite = {
  id: string;
  title: string;
  author: string;
  cover_url: string | null;
  user_id: string;
  exchange_mode: string | null;
  is_donation: boolean | null;
};

type Row = {
  id: string;
  status: string;
  created_at: string;
  requester_id: string;
  book_id: string;
  offered_book_id: string | null;
  owner_picks: boolean | null;
};

function badgeClass(status: string) {
  if (status === "accepted" || status === "completed" || status === "exchanged") return "bg-primary-soft text-primary";
  if (status === "declined" || status === "cancelled") return "bg-destructive/10 text-destructive";
  return "bg-muted text-muted-foreground";
}

function BookTile({ book, label }: { book: BookLite | null | undefined; label: string }) {
  return (
    <div className="min-w-0 flex-1">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
      <div className="mt-2 flex gap-3">
        <div className="h-16 w-11 shrink-0 overflow-hidden rounded-md bg-primary-soft">
          {book?.cover_url ? (
            <img src={book.cover_url} alt={book.title} className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full items-center justify-center"><BookOpen className="h-4 w-4 text-primary/40" /></div>
          )}
        </div>
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold">{book?.title ?? "Not chosen yet"}</p>
          <p className="truncate text-xs text-muted-foreground">{book ? `by ${book.author}` : "Waiting on a pick"}</p>
        </div>
      </div>
    </div>
  );
}

function ExchangesPage() {
  const { user, loading } = useAuth();
  const hydrated = useHydrated();
  const [rows, setRows] = useState<Row[]>([]);
  const [books, setBooks] = useState<Record<string, BookLite>>({});
  const [names, setNames] = useState<Record<string, string>>({});
  const [tab, setTab] = useState<"incoming" | "outgoing">("incoming");
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      const select = "id,status,created_at,requester_id,book_id,offered_book_id,owner_picks";
      const [mine, theirs, myBooks] = await Promise.all([
        supabase.from("exchange_requests").select(select + ",books!inner(user_id)").eq("books.user_id", user.id).order("created_at", { ascending: false }),
        supabase.from("exchange_requests").select(select).eq("requester_id", user.id).order("created_at", { ascending: false }),
        supabase.from("books").select("id").eq("user_id", user.id),
      ]);
      if (cancelled) return;
      const all = [...((mine.data ?? []) as unknown as Row[]), ...((theirs.data ?? []) as unknown as Row[])];
      const uniq = Array.from(new Map(all.map((r) => [r.id, r])).values());
      setRows(uniq);

      const bookIds = Array.from(new Set(uniq.flatMap((r) => [r.book_id, r.offered_book_id]).filter(Boolean) as string[]));
      if (bookIds.length) {
        const { data } = await supabase
          .from("books")
          .select("id,title,author,cover_url,user_id,exchange_mode,is_donation")
          .in("id", bookIds);
        if (!cancelled) {
          const map: Record<string, BookLite> = {};
          ((data ?? []) as BookLite[]).forEach((b) => { map[b.id] = b; });
          setBooks(map);
        }
      }

      const { data: profiles } = await supabase.rpc("get_public_profiles" as never);
      if (!cancelled) {
        const map: Record<string, string> = {};
        ((profiles ?? []) as { id: string; full_name: string | null }[]).forEach((p) => { map[p.id] = p.full_name ?? "A reader"; });
        setNames(map);
      }
      // Ownership of a request is decided by the requested book's owner.
      void myBooks;
      if (!cancelled) setReady(true);
    })();
    return () => { cancelled = true; };
  }, [user]);

  if (!hydrated || loading)
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      </div>
    );
  if (!user)
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="mx-auto max-w-md px-4 py-24 text-center">
          <h1 className="text-2xl font-bold">Chosen books</h1>
          <p className="mt-2 text-sm text-muted-foreground">Sign in to see the books picked in return for each swap.</p>
          <Link to="/login" className="mt-6 inline-block rounded-xl bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground">Sign in</Link>
        </div>
      </div>
    );


  const visible = rows.filter((r) =>
    tab === "outgoing" ? r.requester_id === user.id : books[r.book_id]?.user_id === user.id,
  );

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="mx-auto max-w-4xl px-4 py-10">
        <h1 className="flex items-center gap-2 text-3xl font-bold">
          <ArrowLeftRight className="h-7 w-7 text-primary" /> Chosen books
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Every request side by side with the book picked in return — no need to open your email.
        </p>

        <div className="mt-6 inline-flex rounded-full bg-muted p-1">
          {(["incoming", "outgoing"] as const).map((k) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${tab === k ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}
            >
              {k === "incoming" ? "Requests on my books" : "My requests"}
            </button>
          ))}
        </div>

        {visible.length === 0 ? (
          <div className="mt-8 rounded-2xl bg-card p-10 text-center shadow-[var(--shadow-card)]">
            <BookOpen className="mx-auto h-10 w-10 text-primary/50" />
            <p className="mt-3 text-sm text-muted-foreground">{ready ? "Nothing here yet." : "Loading…"}</p>
          </div>
        ) : (
          <div className="mt-6 space-y-3">
            {visible.map((r) => {
              const requested = books[r.book_id];
              const chosen = r.offered_book_id ? books[r.offered_book_id] : null;
              const mode = ((requested?.exchange_mode as ExchangeMode) ?? (requested?.is_donation ? "donate" : "swap")) as ExchangeMode;
              const other = tab === "incoming" ? names[r.requester_id] : names[requested?.user_id ?? ""];
              return (
                <div key={r.id} className="rounded-2xl bg-card p-4 shadow-[var(--shadow-card)]">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="text-sm text-muted-foreground">
                      {modeLabel(mode)} · {tab === "incoming" ? `From ${other ?? "a reader"}` : `Owner: ${other ?? "a reader"}`} · {new Date(r.created_at).toLocaleDateString()}
                    </p>
                    <span className={`rounded-full px-2 py-0.5 text-xs font-medium capitalize ${badgeClass(r.status)}`}>{r.status}</span>
                  </div>
                  <div className="mt-3 flex flex-wrap items-start gap-4">
                    <BookTile book={requested} label="Requested book" />
                    {mode === "swap" && (
                      <>
                        <ArrowLeftRight className="mt-8 hidden h-4 w-4 shrink-0 text-primary sm:block" />
                        <BookTile book={chosen} label="Chosen in return" />
                      </>
                    )}
                    <div className="self-center">
                      <Link to="/requests/$id" params={{ id: r.id }}>
                        <Button size="sm" variant="outline">Open</Button>
                      </Link>
                    </div>
                  </div>
                  {mode === "swap" && !chosen && r.status === "pending" && (
                    <p className="mt-3 text-xs text-muted-foreground">
                      {r.owner_picks ? "The owner still needs to pick a book from their shelf." : "No book chosen in return yet."}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
